/**
 * ResponseDisplay Component
 *
 * Displays API test response with status code, response time, headers, and formatted body.
 * Provides copy-to-clipboard functionality and color-coded status indicators.
 *
 * Requirements: 18.1, 18.2, 18.3, 18.4, 18.5, 18.6
 */

import React, { useState } from 'react';
import { ApiTestResponse } from '../types';
import { formatResponseBody } from '../utils';

export interface ResponseDisplayProps {
  /** API test response to display */
  response: ApiTestResponse | null;
}

/**
 * ResponseDisplay component displays API test responses with formatting and syntax highlighting
 */
export const ResponseDisplay: React.FC<ResponseDisplayProps> = ({ response }) => {
  const [headersExpanded, setHeadersExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  if (!response) {
    return null;
  }

  const handleCopyToClipboard = () => {
    const bodyText =
      typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2);

    navigator.clipboard.writeText(bodyText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // Determine status color using design system
  const statusBadgeClass = response.success ? 'badge-success' : 'badge-error';
  const statusBgClass = response.success ? 'bg-success-50' : 'bg-error-50';

  // Format response body
  const contentType =
    response.headers['content-type'] || response.headers['Content-Type'] || 'text/plain';
  const { formatted, language } = formatResponseBody(response.body, contentType);

  // Extract Gateway-specific diagnostic headers (case-insensitive lookup)
  const getHeader = (name: string): string | undefined => {
    const lower = name.toLowerCase();
    for (const [key, val] of Object.entries(response.headers)) {
      if (key.toLowerCase() === lower) return String(val);
    }
    return undefined;
  };

  const cacheStatus = getHeader('x-cache-status');
  const rateLimitRemaining = getHeader('x-ratelimit-remaining-minute');
  const rateLimitLimit = getHeader('x-ratelimit-limit-minute');
  const proxyLatency = getHeader('x-kong-proxy-latency');
  const upstreamLatency = getHeader('x-kong-upstream-latency');

  return (
    <div className="space-y-4">
      {/* Status and Response Time */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <span
            className={`badge-modern ${statusBadgeClass}`}
            style={{
              fontSize: 'var(--text-lg)',
              padding: 'var(--space-2) var(--space-4)',
            }}
          >
            {response.statusCode} {response.statusText}
          </span>
          {response.success && (
            <span className="text-success-600 flex items-center gap-1">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                Sucesso
              </span>
            </span>
          )}
        </div>
        <div
          className="flex items-center gap-2 text-gray-600"
          style={{ fontSize: 'var(--text-sm)' }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 14A6 6 0 108 2a6 6 0 000 12z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 4v4l2 2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span style={{ fontWeight: 'var(--font-semibold)' }}>
            {response.responseTime.toFixed(2)} ms
          </span>
        </div>
      </div>

      {/* Quick Gateway Diagnostic Badges */}
      {(cacheStatus || rateLimitRemaining || proxyLatency) && (
        <div className="flex flex-wrap items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mr-1">
            Gateway:
          </span>
          {cacheStatus && (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold font-mono border ${
                cacheStatus.toUpperCase() === 'HIT'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800'
                  : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800'
              }`}
            >
              Cache: {cacheStatus.toUpperCase()}
            </span>
          )}
          {rateLimitRemaining && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold font-mono bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800">
              Restante/min: {rateLimitRemaining}
              {rateLimitLimit ? ` / ${rateLimitLimit}` : ''}
            </span>
          )}
          {proxyLatency && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold font-mono bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-700/60 dark:text-slate-300 dark:border-slate-600">
              Proxy: {proxyLatency}ms
            </span>
          )}
          {upstreamLatency && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold font-mono bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-700/60 dark:text-slate-300 dark:border-slate-600">
              Upstream: {upstreamLatency}ms
            </span>
          )}
        </div>
      )}

      {/* Error Message (if present) */}
      {response.error && (
        <div
          className={`${statusBgClass} border rounded-lg p-4`}
          style={{ borderColor: 'var(--color-error-200)' }}
        >
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              style={{ color: 'var(--color-error-600)' }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4
                style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--color-error-800)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Erro
              </h4>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error-700)' }}>
                {response.error}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Response Headers (Collapsible) */}
      <div
        className="border rounded-lg overflow-hidden"
        style={{ borderColor: 'var(--color-gray-200)', borderRadius: 'var(--radius-lg)' }}
      >
        <button
          onClick={() => setHeadersExpanded(!headersExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between text-left transition-colors"
          style={{
            backgroundColor: 'var(--color-gray-50)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--color-gray-700)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-100)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-50)')}
        >
          <span>Headers da Resposta ({Object.keys(response.headers).length})</span>
          <svg
            className="w-5 h-5 transition-transform"
            style={{ transform: headersExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {headersExpanded && (
          <div className="p-4 bg-white border-t" style={{ borderColor: 'var(--color-gray-200)' }}>
            <div className="space-y-2">
              {Object.entries(response.headers).map(([key, value]) => (
                <div key={key} className="flex text-sm">
                  <span
                    className="w-1/3"
                    style={{
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--color-gray-700)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    {key}:
                  </span>
                  <span
                    className="w-2/3 break-all"
                    style={{
                      color: 'var(--color-gray-600)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Response Body */}
      <div
        className="border rounded-lg overflow-hidden"
        style={{ borderColor: 'var(--color-gray-200)', borderRadius: 'var(--radius-lg)' }}
      >
        <div
          className="px-4 py-3 flex items-center justify-between"
          style={{ backgroundColor: 'var(--color-gray-50)' }}
        >
          <span
            style={{
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-gray-700)',
              fontSize: 'var(--text-sm)',
            }}
          >
            Corpo da Resposta
          </span>
          <button onClick={handleCopyToClipboard} className="btn-base btn-ghost btn-sm">
            {copySuccess ? (
              <>
                <svg
                  className="w-4 h-4"
                  style={{ color: 'var(--color-success-600)' }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Copiado!
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Copiar
              </>
            )}
          </button>
        </div>
        <div className="p-4 overflow-x-auto" style={{ backgroundColor: 'var(--color-gray-900)' }}>
          <pre
            className={`language-${language}`}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-gray-100)',
              lineHeight: 'var(--leading-relaxed)',
              margin: 0,
            }}
          >
            <code>{formatted}</code>
          </pre>
        </div>
      </div>

      {/* Timestamp */}
      <div
        className="text-right"
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-gray-500)',
        }}
      >
        {new Date(response.timestamp).toLocaleString('pt-PT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </div>
    </div>
  );
};
